// Small reusable hooks: load a status-filtered queue, manage bulk selection,
// drag a dialog out of the way, and track an upload through extraction.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useFieldContext_unstable } from '@fluentui/react-components'
import type { CSSProperties, DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'
import { isApiError } from '../api/errors'
import type { Invoice, InvoiceLineItem, LineItemDetailKind, Upload } from './types'
import { getUpload, loadDepartmentLabels, loadAccountDetailKinds, loadAccountLabels, loadInactiveAccountIds, loadInvoicesByStatus } from './data'
import { HandlingTimer } from './handlingTimer'
import { recordViewSession } from './viewSessions'
import type { InvoiceStatus } from './constants'
import { INITIAL_REFRESH, queueRefreshAction, type RefreshSignal } from './refresh'
import { inactiveAccountReasons, linesOnInactiveAccount } from './required'

interface QueueState {
  invoices: Invoice[]
  loading: boolean
  error: string | null
  /** silent: refresh the rows without flipping into the loading state (used by
      the broker fence so the table doesn't flash a spinner on every re-check).
      Resolves with the rows this call fetched — the fence's give-up reads them
      directly rather than a possibly-stale state snapshot. */
  reload: (opts?: ReloadOptions) => Promise<Invoice[]>
}

interface ReloadOptions {
  /** Keep the rows on screen; do not flip into the loading state. */
  silent?: boolean
  /** A refresh nobody asked for (the Pipeline button, the 30-second timer): a
      failure keeps the rows and does not show the error banner. */
  background?: boolean
}

interface QueueOptions {
  /** True while the screen's invoice modal or any of its dialogs is open, or a
      submit is in flight. A refresh signal that arrives meanwhile only marks the
      gallery stale; it catches up silently once this turns false, so nothing
      moves under an open modal (D-160). */
  paused?: boolean
}

/**
 * A DIALOG'S OWN REFUSAL, cleared when the dialog next opens.
 *
 * A save refusal belongs beside the attempt that caused it, inside the dialog.
 * Routed to the screen instead, it renders in a banner BEHIND the modal, on a
 * page the modal has locked from scrolling — the reader sees a few truncated
 * words, or nothing at all, and a button that appears to do nothing. It also
 * outlives the dialog, so a refusal the reader cancelled out of is still on the
 * page afterwards.
 *
 * Written for References on 2026-09-21 (QA wave B1, B1-05) and lifted here on
 * 2026-09-22 when the same defect was found on Rules (QA wave A, A-01), which
 * had not been brought along. ONE hook, so the two screens cannot answer
 * differently again.
 *
 * `row` is whatever identifies the record the dialog is open on — passing it
 * is what makes "cleared the next time the dialog opens" true.
 */
export function useDialogError(row: unknown): [string | null, (m: string | null) => void] {
  const [message, setMessage] = useState<string | null>(null)
  useEffect(() => {
    setMessage(null)
  }, [row])
  return [message, setMessage]
}

export function useQueue(
  statuses: readonly string[],
  refresh: RefreshSignal = INITIAL_REFRESH,
  options: QueueOptions = {},
): QueueState {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Stable key so the effect doesn't re-run on every render from a new array ref.
  const key = statuses.join('|')
  // Monotonic request id: only the latest reload may write state, so two loads
  // resolving out of order can't leave the older payload showing.
  const seqRef = useRef(0)

  const reload = useCallback((opts?: ReloadOptions): Promise<Invoice[]> => {
    if (!opts?.silent) setLoading(true)
    // A background refresh leaves the banner alone until it has an answer: it
    // clears on success and never raises one on failure.
    if (!opts?.background) setError(null)
    const seq = ++seqRef.current
    return loadInvoicesByStatus(statuses)
      .then((rows) => {
        if (seq === seqRef.current) {
          setInvoices(rows)
          if (opts?.background) setError(null)
        }
        return rows
      })
      .catch((e: Error) => {
        if (seq === seqRef.current && !opts?.background) setError(e.message)
        return [] as Invoice[]
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // First load, and again if the status set changes. Never silent: there is
  // nothing on screen yet to keep.
  useEffect(() => {
    reload()
  }, [reload])

  // THE SHELL'S REFRESH SIGNAL (D-160): the Refresh button (not silent), the
  // Pipeline button and the 30-second timer (both silent). While `paused`, a
  // signal only marks the gallery stale, and the catch-up runs silently once
  // the modal or dialog closes. `queueRefreshAction` holds the rule.
  const paused = !!options.paused
  const seenSeqRef = useRef<number | null>(null)
  const staleRef = useRef(false)
  useEffect(() => {
    if (seenSeqRef.current === null) {
      // Mount: the first-load effect above already covers this signal.
      seenSeqRef.current = refresh.seq
      return
    }
    const seqChanged = refresh.seq !== seenSeqRef.current
    seenSeqRef.current = refresh.seq
    const action = queueRefreshAction({ seqChanged, silent: refresh.silent, paused, stale: staleRef.current })
    if (action === 'mark-stale') staleRef.current = true
    else if (action === 'reload') {
      staleRef.current = false
      void reload()
    } else if (action === 'reload-silent') {
      staleRef.current = false
      void reload({ silent: true, background: true })
    }
  }, [refresh.seq, refresh.silent, paused, reload])

  return { invoices, loading, error, reload }
}

// PORT NOTE — the ~200-line "broker catch-up fence" that lived here is gone,
// along with the appointment poller. Both existed only because state advanced
// out of band: the client marked a row "submitting…", re-polled at 2, 4, 6, 8,
// 10, 15, 20 and 30 seconds, and gave up at 32. The decision endpoints are
// synchronous and return the new status, so a decision is either applied (and
// the response says to what) or refused with the reason. Do not reintroduce an
// optimistic status flip in its place.

interface Selection {
  selected: Set<string>
  toggle: (id: string) => void
  toggleAll: () => void
  /** Bulk-set membership for a group of ids (per-company Select all — app.md §9 step 9). */
  setMany: (ids: string[], on: boolean) => void
  clear: () => void
  allSelected: boolean
  count: number
}

/**
 * THE SELECTION ALGEBRA, WITHOUT REACT.
 *
 * Every rule `useSelection` enforces is one of these five pure functions, and
 * the hook below is the state container around them and nothing else. Split
 * out so the harness can exercise the rules directly: the app's test suite runs
 * on `environment: 'node'` with no renderer, so a hook cannot be driven there,
 * and until this split the one piece of shared state behind every bulk action
 * on every queue had no unit test at all.
 *
 * `selected` is the RAW set the hook holds — it may name ids that have since
 * left the queue. `ids` is what is selectable right now. Keeping the two apart
 * is the whole design: nothing is pruned on a reload, so a row that leaves and
 * comes back (a filter narrowed, then widened) keeps its check, while
 * `validSelection` makes sure a departed id is never acted on.
 */
export const selectionRules = {
  /** The selection as it may actually be ACTED on: raw ∩ selectable. */
  validSelection(selected: ReadonlySet<string>, ids: readonly string[]): Set<string> {
    const selectable = new Set(ids)
    return new Set([...selected].filter((id) => selectable.has(id)))
  },
  /** Flip one id's membership. */
  toggle(selected: ReadonlySet<string>, id: string): Set<string> {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  },
  /** Select all / none: all selectable ids already selected means clear. An
      EMPTY selectable list clears too — `[].every()` is true — which is what
      keeps Select all inert on an empty queue. */
  toggleAll(selected: ReadonlySet<string>, ids: readonly string[]): Set<string> {
    return ids.every((id) => selected.has(id)) ? new Set() : new Set(ids)
  },
  /** Add or remove a group at once (per-company Select all — app.md §9 step 9). */
  setMany(selected: ReadonlySet<string>, toSet: readonly string[], on: boolean): Set<string> {
    const next = new Set(selected)
    toSet.forEach((id) => (on ? next.add(id) : next.delete(id)))
    return next
  },
  /** Whether the Select all box reads checked. FALSE on an empty selectable
      list, unlike `toggleAll`'s own `every` — a queue with nothing to select
      is not "all selected". */
  allSelected(selected: ReadonlySet<string>, ids: readonly string[]): boolean {
    return ids.length > 0 && ids.every((id) => selected.has(id))
  },
}

export function useSelection(ids: string[]): Selection {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Derive the valid selection (intersected with the current queue) rather than
  // pruning in an effect — ids that left the queue are simply ignored.
  const idSet = useMemo(() => new Set(ids), [ids])
  const valid = useMemo(
    () => selectionRules.validSelection(selected, [...idSet]),
    [selected, idSet],
  )

  const toggle = useCallback((id: string) => {
    setSelected((prev) => selectionRules.toggle(prev, id))
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) => selectionRules.toggleAll(prev, ids))
  }, [ids])

  const setMany = useCallback((toSet: string[], on: boolean) => {
    setSelected((prev) => selectionRules.setMany(prev, toSet, on))
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  return {
    selected: valid,
    toggle,
    toggleAll,
    setMany,
    clear,
    allSelected: selectionRules.allSelected(selected, ids),
    count: valid.size,
  }
}

// ---------------------------------------------------------------------------
// Half labels
// ---------------------------------------------------------------------------

/** Id -> label for the two halves of a code, or the id itself while the
    masters are still loading and for an id the masters do not carry. */
interface HalfLabels {
  account: (id: string | null | undefined) => string | undefined
  department: (id: string | null | undefined) => string | undefined
  /** Owner item 11 (2026-08-19) — the Marketing trigger for a line's Additional
      Detail (D-154; Transportation's is the line's source, D-381):
      `undefined` when the id is missing/unknown or carries no `detail_kind`,
      never a hard-coded account id. */
  detailKind: (id: string | null | undefined) => LineItemDetailKind | undefined
  /** The Account is Inactive (D-175) — a line holding it with no Department
      yet needs a different Account. */
  accountInactive: (id: string | null | undefined) => boolean
}

/**
 * The labels for "What is it?" and "Who is it for?".
 *
 * A line item carries ids and nothing else, so every surface that shows a half
 * has to resolve the label itself. Without this a line holding `67-502` rendered
 * an EMPTY picker — a line that is coded reading as one nobody has touched, at
 * the steps whose whole job is to tell those apart.
 *
 * The fallback is the id, never blank: an id is legible and an empty box is a
 * lie. `data.ts` memoizes both reads for the session, so mounting this on every
 * row of every table costs two requests in total.
 */
export function useHalfLabels(): HalfLabels {
  const [accounts, setAccounts] = useState<ReadonlyMap<string, string>>(EMPTY_LABELS)
  const [departments, setDepartments] = useState<ReadonlyMap<string, string>>(EMPTY_LABELS)
  const [detailKinds, setDetailKinds] = useState<ReadonlyMap<string, LineItemDetailKind>>(EMPTY_DETAIL_KINDS)
  const [inactiveAccounts, setInactiveAccounts] = useState<ReadonlySet<string>>(EMPTY_IDS)

  useEffect(() => {
    let live = true
    // Best-effort: a failed master read leaves the ids showing, which is the
    // same thing this hook falls back to for an unknown id.
    void loadAccountLabels().then((m) => { if (live) setAccounts(m) }).catch(() => {})
    void loadDepartmentLabels().then((m) => { if (live) setDepartments(m) }).catch(() => {})
    void loadAccountDetailKinds().then((m) => { if (live) setDetailKinds(m) }).catch(() => {})
    void loadInactiveAccountIds().then((ids) => { if (live) setInactiveAccounts(ids) }).catch(() => {})
    return () => { live = false }
  }, [])

  return useMemo(
    () => ({
      account: (id) => (id ? (accounts.get(id) ?? id) : undefined),
      department: (id) => (id ? (departments.get(id) ?? id) : undefined),
      detailKind: (id) => (id ? detailKinds.get(id) : undefined),
      accountInactive: (id) => !!id && inactiveAccounts.has(id),
    }),
    [accounts, departments, detailKinds, inactiveAccounts],
  )
}

/**
 * THE MODAL'S LINES, AS THE APPROVE BUTTON'S REASON NEEDS THEM (owner,
 * 2026-10-07 — D-308). The queue row carries counts, not lines, so it cannot
 * say a line's Account went Inactive. Pass `onLineItemsChange` to
 * `InvoiceDetailModal`; `reasons` then names each Inactive Account a line holds,
 * for `codingGateReason` — but only where `accountEditable`, because where the
 * Account is locked the invoice proceeds as it is.
 */
export function useInactiveAccountReasons(
  invoiceId: string | null | undefined,
  accountEditable: boolean,
): {
  reasons: string[]
  onLineItemsChange: (lineItems: InvoiceLineItem[]) => void
} {
  const labels = useHalfLabels()
  const [lines, setLines] = useState<InvoiceLineItem[]>([])
  const reasons = useMemo(
    () =>
      accountEditable
        ? inactiveAccountReasons(
            linesOnInactiveAccount(
              lines
                .filter((l) => l.invoice_id === invoiceId && l.is_active !== false)
                .map((l) => ({ lineNumber: l.line_number ?? 0, accountId: l.account_id })),
              (id) => labels.accountInactive(id),
              (id) => labels.account(id),
            ),
          )
        : [],
    [lines, invoiceId, labels, accountEditable],
  )
  return { reasons, onLineItemsChange: setLines }
}

const EMPTY_LABELS: ReadonlyMap<string, string> = new Map()
const EMPTY_DETAIL_KINDS: ReadonlyMap<string, LineItemDetailKind> = new Map()
const EMPTY_IDS: ReadonlySet<string> = new Set()

// ---------------------------------------------------------------------------
// Draggable dialog (owner item 2, 2026-08-31)
// ---------------------------------------------------------------------------

/** How far an arrow key nudges the dialog, in CSS pixels. */
const DRAG_KEY_STEP = 24
/** How close the dialog may get to a viewport edge before it is clamped. */
export const DRAG_EDGE_MARGIN = 8

const ORIGIN: DragOffset = Object.freeze({ x: 0, y: 0 })

interface DragOffset {
  readonly x: number
  readonly y: number
}

/** The box the surface occupies with NO offset applied — measured once per
    gesture so a clamp never has to re-measure a surface whose transform is one
    React render behind the pointer. */
interface DragBase {
  left: number
  top: number
  width: number
  height: number
}

interface DraggableDialog {
  /** Spread onto the `DialogSurface`'s `style`. */
  surfaceStyle: CSSProperties
  /** Spread onto the `DialogSurface`: its accessible name, the dialog's title
      (QA 2026-09-30, T-05). */
  surfaceProps: { 'aria-label': string }
  /**
   * Spread onto the DRAG HANDLE and nothing else — the `DialogTitle`.
   *
   * Deliberately NOT the surface or the content: `DialogContent` holds the
   * Upload drop zone, whose `onClick` opens the file picker and whose `onDrop`
   * takes an HTML5 file drop. A pointer handler with a `preventDefault` over
   * that subtree breaks both, and the failure looks like "the drop zone stopped
   * working" rather than like a drag bug.
   */
  handleProps: {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void
    onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void
    tabIndex: number
    /** The title's own words, so the drag hint in `title` is its
        description, never its name (QA 2026-09-30, T-05). */
    'aria-label': string
    title: string
    style: CSSProperties
  }
  /**
   * Passed as the `DialogSurface`'s `backdrop` (D-244). The dialog is modal, so
   * the dimmed page behind it is the backdrop; a PDF dragged in from another
   * window and let go a little outside the drop zone lands on it, and the
   * browser's default for a dropped file is to open it in place of the app.
   * These two handlers refuse the drop there instead: the cursor shows "no
   * drop", and nothing happens. The drop zone inside the surface is untouched.
   */
  backdropProps: {
    onDragOver: (e: ReactDragEvent<HTMLDivElement>) => void
    onDrop: (e: ReactDragEvent<HTMLDivElement>) => void
  }
}

/** Declared once, so the backdrop gets the same two handlers every render. */
const REFUSE_DROP: DraggableDialog['backdropProps'] = {
  onDragOver: (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'none'
  },
  onDrop: (e) => e.preventDefault(),
}

/** Keep a dragged surface inside the viewport, so it cannot be parked
    off-screen and stranded. Exported for its own test — there is no DOM in the
    harness, so the base box is supplied rather than measured. */
export function clampToViewport(base: DragBase, want: DragOffset): DragOffset {
  if (typeof window === 'undefined') return want
  const m = DRAG_EDGE_MARGIN
  const minX = m - base.left
  const maxX = window.innerWidth - m - (base.left + base.width)
  const minY = m - base.top
  const maxY = window.innerHeight - m - (base.top + base.height)
  // A surface taller or wider than the viewport inverts the pair; Math.min /
  // Math.max keeps the band between the two edges usable instead of empty.
  return {
    x: Math.max(Math.min(minX, maxX), Math.min(Math.max(minX, maxX), want.x)),
    y: Math.max(Math.min(minY, maxY), Math.min(Math.max(minY, maxY), want.y)),
  }
}

/**
 * Makes one Fluent dialog draggable by its title bar.
 *
 * Why: the Upload dialog covers the window the file is being dragged FROM, and
 * a modal that cannot be moved forces the user to close it, arrange the
 * windows, and open it again. The dialog is clamped inside the viewport, so it
 * cannot be dragged off-screen and stranded, and it re-centres every time it
 * opens.
 *
 * The offset is applied as a `transform`, and only while it is non-zero — a
 * transformed element is a containing block for `position: fixed` descendants,
 * which is exactly what a Fluent popup mounted at the surface relies on. Keep
 * that in mind before putting a `Combobox` or a `Popover` inside a dialog this
 * hook is driving (`app/README.md` → Fluent UI v9 gotchas).
 */
export function useDraggableDialog(open: boolean, name: string): DraggableDialog {
  // The surface is resolved from the handle rather than handed in on a ref —
  // the same `.closest('[role="dialog"]')` self-resolution `popupPositioning.ts`
  // uses. It keeps a ref out of this hook's RESULT, which matters: a hook that
  // hands back a ref object makes every read of its result look to
  // `react-hooks/refs` like a ref access during render.
  const elementRef = useRef<HTMLElement | null>(null)
  const [offset, setOffsetState] = useState<DragOffset>(ORIGIN)
  const [dragging, setDragging] = useState(false)
  const offsetRef = useRef<DragOffset>(ORIGIN)
  const draggingRef = useRef(false)
  const startRef = useRef<{ pointerX: number; pointerY: number; from: DragOffset; base: DragBase } | null>(null)

  const setOffset = useCallback((next: DragOffset) => {
    offsetRef.current = next
    setOffsetState(next)
  }, [])

  /** The dialog surface this handle belongs to, remembered for the resize pass. */
  const resolveSurface = useCallback((handle: HTMLElement | null): HTMLElement | null => {
    const el = handle?.closest?.('[role="dialog"], [role="alertdialog"]') as HTMLElement | null
    if (el) elementRef.current = el
    return el ?? elementRef.current
  }, [])

  /** The untransformed box, from the live element and the offset it is showing. */
  const measure = useCallback((el: HTMLElement | null): DragBase | null => {
    if (!el) return null
    const rect = el.getBoundingClientRect()
    const cur = offsetRef.current
    return { left: rect.left - cur.x, top: rect.top - cur.y, width: rect.width, height: rect.height }
  }, [])

  // Every open starts centred: a dialog parked at an edge last time is not
  // where the next opener will look for it.
  useEffect(() => {
    if (!open) return
    // The surface is a new element every open (Fluent unmounts it on close), so
    // the remembered one is detached and must not be measured.
    elementRef.current = null
    setOffset(ORIGIN)
  }, [open, setOffset])

  // A window resize can leave the dialog outside the viewport; pull it back.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return
    const onResize = () => {
      const el = elementRef.current
      const base = el?.isConnected ? measure(el) : null
      if (base) setOffset(clampToViewport(base, offsetRef.current))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open, measure, setOffset])

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    // The title's `action` slot holds the close button. A press there is a
    // click, not the start of a drag.
    const target = e.target as HTMLElement | null
    if (target?.closest?.('button, a, input, textarea, select, [role="button"]')) return
    const base = measure(resolveSurface(e.currentTarget))
    if (!base) return
    startRef.current = { pointerX: e.clientX, pointerY: e.clientY, from: offsetRef.current, base }
    draggingRef.current = true
    setDragging(true)
    // Pointer capture keeps the moves coming to the handle even when the
    // pointer outruns it, so no window-level listener has to be registered
    // (and none can be leaked).
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [measure, resolveSurface])

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const start = startRef.current
    if (!draggingRef.current || !start) return
    setOffset(
      clampToViewport(start.base, {
        x: start.from.x + (e.clientX - start.pointerX),
        y: start.from.y + (e.clientY - start.pointerY),
      }),
    )
  }, [setOffset])

  const endDrag = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    startRef.current = null
    setDragging(false)
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  const onKeyDown = useCallback((e: ReactKeyboardEvent<HTMLElement>) => {
    let dx = 0
    let dy = 0
    if (e.key === 'ArrowLeft') dx = -DRAG_KEY_STEP
    else if (e.key === 'ArrowRight') dx = DRAG_KEY_STEP
    else if (e.key === 'ArrowUp') dy = -DRAG_KEY_STEP
    else if (e.key === 'ArrowDown') dy = DRAG_KEY_STEP
    else if (e.key === 'Home') {
      e.preventDefault()
      setOffset(ORIGIN)
      return
    } else {
      // Everything else, Escape included, is left alone — Fluent's own surface
      // handler closes the dialog on Escape and skips an event whose default
      // has been prevented.
      return
    }
    const base = measure(resolveSurface(e.currentTarget))
    if (!base) return
    e.preventDefault()
    setOffset(clampToViewport(base, { x: offsetRef.current.x + dx, y: offsetRef.current.y + dy }))
  }, [measure, resolveSurface, setOffset])

  const moved = offset.x !== 0 || offset.y !== 0

  return {
    surfaceStyle: { transform: moved ? `translate(${offset.x}px, ${offset.y}px)` : undefined },
    // NAMED BY ITS TITLE (QA 2026-09-30, T-05). The drag hint rides on the
    // handle's `title`, the hover tooltip, and the Upload pop-up was read as a
    // nameless dialog whose heading said "Drag to move this window…". The
    // title's words now name both the surface and the handle, and the hint
    // stays, as the handle's description.
    surfaceProps: { 'aria-label': name },
    backdropProps: REFUSE_DROP,
    handleProps: {
      'aria-label': name,
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      tabIndex: 0,
      title: 'Drag to move this window, or use the arrow keys. Home re-centres it.',
      style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' },
    },
  }
}

// ---------------------------------------------------------------------------
// Upload progress (owner item 5, 2026-08-31)
// ---------------------------------------------------------------------------

/**
 * What an uploader is shown while a document is being processed.
 *
 * EVERY STAGE IS THE SERVER'S OWN ANSWER (owner, 2026-09-03). Until that date
 * there was a `processing` stage the client invented: still the server's
 * `Uploaded`, relabelled once the row had been read at least once and time had
 * passed. It stood in for the whole pipeline — the page split, every extraction
 * call and the queue wait — because no finer signal existed, and `parsed`
 * arrived while pass 2 was still raising flags, so "Done" was shown over work
 * that had not happened yet.
 *
 * `uploads.processing_phase` now carries the four real phases, so the guess is
 * gone: `received` means the row is queued and NOTHING has picked it up, which
 * is a fact rather than an inference.
 */
export type UploadStage =
  | 'uploaded'
  | 'splitting'
  | 'extracting'
  | 'matching'
  | 'finishing'
  | 'parsed'
  | 'failed'
  | 'unknown'

export interface UploadProgress {
  stage: UploadStage
  /** Invoices found in the document. Only meaningful once `parsed`. */
  recordCount: number | null
}

/** First wait before the first read. */
export const POLL_FIRST_MS = 1_500
const POLL_BACKOFF = 1.5
/** The ceiling the backoff climbs to — one read every 8s, not a hammering. */
export const POLL_MAX_MS = 8_000
/**
 * Stop asking after this long. The work continues server-side; the row says so.
 *
 * RAISED from 180s to 10 minutes (owner, 2026-09-03) because the window this
 * has to cover grew: it used to end at `Parsed`, and now it runs on through
 * matching, which is one pass-2 call per invoice. A 121-page packet holds 58
 * documents, so the old ceiling would have given up mid-packet and reported
 * "check the queues shortly" on an upload that was working perfectly.
 */
export const POLL_GIVE_UP_MS = 600_000

export function nextPollDelay(previous: number): number {
  return Math.min(Math.round(previous * POLL_BACKOFF), POLL_MAX_MS)
}

/** No more polling once a row reaches one of these — the answer will not change. */
export function isTerminalStage(stage: UploadStage | undefined): boolean {
  return stage === 'parsed' || stage === 'failed' || stage === 'unknown'
}

/** The row cannot be read (403 without document access, 404 outside scope) or
    we stopped asking. Extraction is unaffected either way. */
export const UPLOAD_UNREADABLE: UploadProgress = Object.freeze({
  stage: 'unknown',
  recordCount: null,
})

/** The four server phases, in pipeline order, as this module's stage names. */
const STAGE_FOR_PHASE = {
  Splitting: 'splitting',
  Extracting: 'extracting',
  Matching: 'matching',
  Finishing: 'finishing',
} as const satisfies Record<NonNullable<Upload['processing_phase']>, UploadStage>

/**
 * One upload row → one stage, from `(status, processing_phase)` and nothing
 * else. No elapsed time, no read count, no inference.
 *
 * ORDER MATTERS HERE. A failure outranks any phase left on the row. A phase
 * outranks `status`, because the extract worker sets `Parsed` and `Matching` in
 * ONE write — reading `status` first would report "Done" the instant extraction
 * finished, which is the bug this replaced. `record_count` is carried through
 * the matching stages so the toast can already say how many invoices came out.
 */
export function uploadStage(upload: Upload): UploadProgress {
  const recordCount = upload.record_count ?? null
  if (upload.status === 'Failed') return { stage: 'failed', recordCount: null }
  const phase = upload.processing_phase ?? null
  if (phase !== null && phase in STAGE_FOR_PHASE) {
    return { stage: STAGE_FOR_PHASE[phase], recordCount }
  }
  if (upload.status === 'Parsed') return { stage: 'parsed', recordCount }
  if (upload.status === 'Uploaded') return { stage: 'uploaded', recordCount: null }
  return UPLOAD_UNREADABLE
}

const EMPTY_PROGRESS: ReadonlyMap<string, UploadProgress> = new Map()

/**
 * Polls `GET /uploads/{id}` for each registered upload until it settles.
 *
 * Stops on the first of: every tracked upload terminal, `active` going false
 * (the dialog closed), the component unmounting, or `POLL_GIVE_UP_MS`. The
 * interval backs off from `POLL_FIRST_MS` to `POLL_MAX_MS`. The only timer is
 * the one this effect owns, and the teardown clears it and fences every
 * in-flight read, so a dialog closed and reopened cannot end up with two loops.
 *
 * Nothing here is logged. The row carries statuses, counts and ids and nothing
 * else — no file content, no extracted values, no vendor or company names.
 */
export function useUploadProgress(
  uploadIds: readonly string[],
  active: boolean,
): ReadonlyMap<string, UploadProgress> {
  const [progress, setProgress] = useState<ReadonlyMap<string, UploadProgress>>(EMPTY_PROGRESS)
  const progressRef = useRef<ReadonlyMap<string, UploadProgress>>(EMPTY_PROGRESS)

  // Stable key so adding a file doesn't restart the loop on every render.
  const key = uploadIds.join('|')

  useEffect(() => {
    const ids = key ? key.split('|') : []
    if (!active || ids.length === 0) return
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = POLL_FIRST_MS
    const startedAt = Date.now()

    const write = (updates: ReadonlyArray<readonly [string, UploadProgress]>) => {
      if (updates.length === 0) return
      const next = new Map(progressRef.current)
      for (const [id, p] of updates) next.set(id, p)
      progressRef.current = next
      setProgress(next)
    }

    const outstanding = (): string[] =>
      ids.filter((id) => !isTerminalStage(progressRef.current.get(id)?.stage))

    const tick = async (): Promise<void> => {
      const pending = outstanding()
      if (pending.length === 0) return
      if (Date.now() - startedAt > POLL_GIVE_UP_MS) {
        write(pending.map((id) => [id, UPLOAD_UNREADABLE] as const))
        return
      }
      const results = await Promise.all(
        pending.map(async (id): Promise<readonly [string, UploadProgress] | null> => {
          try {
            const upload = await getUpload(id)
            return [id, uploadStage(upload)] as const
          } catch (e) {
            // 403 (no document access) and 404 (outside this caller's scope)
            // are final answers, not blips — a References-role uploader gets
            // the 403 on every read. Stop asking and say the work continues.
            if (isApiError(e) && (e.status === 403 || e.status === 404)) {
              return [id, UPLOAD_UNREADABLE] as const
            }
            // Anything else is transient: keep the stage and try again.
            return null
          }
        }),
      )
      if (!live) return
      write(results.filter((r): r is readonly [string, UploadProgress] => r !== null))
      if (outstanding().length === 0) return
      delay = nextPollDelay(delay)
      timer = setTimeout(() => { void tick() }, delay)
    }

    timer = setTimeout(() => { void tick() }, delay)
    return () => {
      live = false
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [key, active])

  return progress
}

// --- Handling time ----------------------------------------------------------

/**
 * Time one viewing of one invoice, and file the result when it closes.
 *
 * WHAT THIS COSTS ON SCREEN: nothing measurable. It renders no state, so no
 * component re-renders while the clock runs; the listeners are passive and each
 * one does a single number assignment; and the arithmetic that decides how much
 * of the session was idle happens once, at the end. There is no interval, no
 * heartbeat, and no request until the modal closes.
 *
 * WHEN A SESSION IS FILED: on close, and on the tab being hidden. The second
 * looks redundant but is the only flush a closing browser will honour, and the
 * timer's `end` is idempotent so filing twice files one session, not two.
 *
 * `status` IS A DEPENDENCY, so a status change while the modal is open closes
 * the session and starts a new one. That is deliberate: the seconds before a
 * decision belong to the step the reviewer was working at and the seconds after
 * belong to the next one, and crediting the whole visit to either would be
 * wrong. In practice a decision closes the modal, so this rarely fires.
 */
export function useInvoiceTimer(
  open: boolean,
  invoiceId: string | undefined,
  status: InvoiceStatus | undefined,
): void {
  useEffect(() => {
    if (!open || invoiceId === undefined || status === undefined) return

    const timer = new HandlingTimer(Date.now())
    let filed = false
    const file = (): void => {
      if (filed) return
      filed = true
      recordViewSession(invoiceId, status, timer.end(Date.now()))
    }

    const onActivity = (): void => {
      timer.activity(Date.now())
    }
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        timer.pause(Date.now())
        // File now rather than hold the session open: a hidden tab may never
        // run another line of our code.
        file()
      } else {
        timer.resume(Date.now())
      }
    }

    // `passive` on all four: none of them calls `preventDefault`, and saying so
    // lets the browser scroll without waiting to find out.
    const options = { passive: true } as const
    window.addEventListener('keydown', onActivity, options)
    window.addEventListener('pointerdown', onActivity, options)
    window.addEventListener('wheel', onActivity, options)
    window.addEventListener('scroll', onActivity, { ...options, capture: true })
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('keydown', onActivity)
      window.removeEventListener('pointerdown', onActivity)
      window.removeEventListener('wheel', onActivity)
      window.removeEventListener('scroll', onActivity, { capture: true })
      document.removeEventListener('visibilitychange', onVisibility)
      file()
    }
  }, [open, invoiceId, status])
}

// A CONTROL INSIDE A LABELLED FLUENT `Field`, NAMED BY THAT LABEL (QA
// 2026-10-04, A-08). Why both attributes: `components/FieldControls.tsx`.
/** The label's text as a screen reader hears it: `aria-hidden` parts dropped. */
function visibleLabelText(labelId: string): string {
  const el = document.getElementById(labelId)
  if (!el) return ''
  const copy = el.cloneNode(true) as HTMLElement
  copy.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove())
  return (copy.textContent ?? '').trim()
}

/**
 * The name a control inside a labelled `Field` should carry, or nothing when
 * the caller names it already or there is no labelled `Field` around it.
 */
export function useFieldLabelName(props: { 'aria-label'?: string; 'aria-labelledby'?: string }): {
  'aria-labelledby'?: string
  'aria-label'?: string
} {
  const labelId = useFieldContext_unstable()?.labelId
  const own = Boolean(props['aria-label'] || props['aria-labelledby'])
  const [text, setText] = useState('')
  // Read once the label is in the DOM, and again whenever its words change —
  // a label can change with a dialog's mode while the Field stays mounted.
  useLayoutEffect(() => {
    const el = !labelId || own ? null : document.getElementById(labelId)
    if (!el || !labelId) return
    const read = () => setText(visibleLabelText(labelId))
    read()
    const watch = new MutationObserver(read)
    watch.observe(el, { childList: true, characterData: true, subtree: true })
    return () => watch.disconnect()
  }, [labelId, own])
  if (!labelId || own) return {}
  return text ? { 'aria-labelledby': labelId, 'aria-label': text } : { 'aria-labelledby': labelId }
}
